import React, { useState } from "react";
import { toast } from "sonner";
import { Briefcase, Lock, Users, Trash2, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { GoldButton, GhostButton } from "./common";
import {
  EMPLOYMENT_STATUS, INDUSTRY, YEARS_EXPERIENCE, EMPLOYER_TENURE, EDUCATION, BUSINESS_OWNERSHIP,
  INCOME_RANGE, INCOME_SOURCES, FINANCIAL_LIFESTYLE, FINANCIAL_GOALS, FINANCE_RELATIONSHIP,
} from "../lib/careerFinancialOptions";

const inputCls = "mt-1 w-full rounded-lg bg-[#0C0A0A] border border-white/10 px-3 py-2 text-sm text-[#FBF8F3] focus:border-[#D4AF37] outline-none";
const FIN_FIELDS = ["income_range", "income_sources", "financial_lifestyle", "financial_goals", "finance_relationship_approach"];

function Sel({ label, testid, value, onChange, options, placeholder = "Select" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">{label}</label>
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value || null)} className={inputCls}>
        <option value="">{placeholder}</option>
        {options.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
      </select>
    </div>
  );
}

function VisibilityToggle({ testid, value, onChange }) {
  return (
    <select data-testid={testid} value={value || "private"} onChange={(e) => onChange(e.target.value)}
      className="mt-1 rounded-lg bg-[#0C0A0A] border border-white/10 px-2 py-1 text-[11px] text-[#C9A96E] focus:border-[#D4AF37] outline-none">
      <option value="private">🔒 Private (only me)</option>
      <option value="members">👥 Approved members</option>
    </select>
  );
}

function ChecklistField({ label, testid, options, values, onToggle, visNode }) {
  return (
    <div className="rounded-lg border border-white/8 bg-[#0C0A0A]/40 p-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">{label}</label>
        {visNode}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {options.map(([k, lbl]) => {
          const on = values.includes(k);
          return (
            <button type="button" key={k} data-testid={`${testid}-${k}`} onClick={() => onToggle(k)}
              className={`rounded-full px-3 py-1 text-xs border transition-colors ${on ? "bg-[#541224] border-[#D4AF37]/50 text-[#FBF8F3]" : "border-white/10 text-[#9E978E] hover:text-[#FBF8F3]"}`}>
              {lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CareerFinancialEditor({ initial, onSaved }) {
  const init = initial || {};
  const [cf, setCf] = useState({
    employment_status: init.employment_status || "", industry: init.industry || "",
    professional_title: init.professional_title || "", years_experience: init.years_experience || "",
    employer_tenure: init.employer_tenure || "", education: init.education || "",
    business_ownership: init.business_ownership || "", career_visibility: init.career_visibility || "members",
    income_range: init.income_range || "", income_sources: init.income_sources || [],
    financial_lifestyle: init.financial_lifestyle || "", financial_goals: init.financial_goals || [],
    finance_relationship_approach: init.finance_relationship_approach || "",
    fin_visibility: {
      income_range: "private", income_sources: "private", financial_lifestyle: "private",
      financial_goals: "private", finance_relationship_approach: "private", ...(init.fin_visibility || {}),
    },
  });
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const set = (k, v) => setCf((c) => ({ ...c, [k]: v }));
  const setVis = (f, v) => setCf((c) => ({ ...c, fin_visibility: { ...c.fin_visibility, [f]: v } }));
  const toggleIn = (k, v) => setCf((c) => {
    const arr = c[k] || [];
    return { ...c, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...cf,
        professional_title: (cf.professional_title || "").trim() || null,
        employment_status: cf.employment_status || null, industry: cf.industry || null,
        years_experience: cf.years_experience || null, employer_tenure: cf.employer_tenure || null,
        education: cf.education || null, business_ownership: cf.business_ownership || null,
        income_range: cf.income_range || null, financial_lifestyle: cf.financial_lifestyle || null,
        finance_relationship_approach: cf.finance_relationship_approach || null,
      };
      await api.put("/profile/career-financial", payload);
      toast.success("Career & Financial Profile saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally { setSaving(false); }
  };

  const removeAll = async () => {
    try {
      await api.delete("/profile/career-financial");
      toast.success("Career & Financial Profile removed");
      setConfirmDel(false);
      onSaved && onSaved();
    } catch (e) { toast.error("Could not remove. Please try again."); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#141113]/60 p-5 mt-6 space-y-5" data-testid="career-financial-editor">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-serif text-xl text-[#FBF8F3] flex items-center gap-2">
            <Briefcase size={18} className="text-[#D4AF37]" /> Career & Financial Profile
          </h3>
          <p className="text-[11px] text-[#9E978E] mt-1 max-w-2xl">
            Completely optional. Share as much or as little as you like to help others gauge career and lifestyle
            compatibility. Everything here is <span className="text-[#C9A96E]">self-reported and not verified</span>.
            It never affects registration, approval, or your account.
          </p>
        </div>
      </div>

      {/* Professional Background */}
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h4 className="text-sm text-[#C9A96E] flex items-center gap-1.5"><Users size={14} /> Professional Background</h4>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[#9E978E]">Career section visibility</span>
            <VisibilityToggle testid="cf-career-visibility" value={cf.career_visibility} onChange={(v) => set("career_visibility", v)} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Sel label="Employment status" testid="cf-employment-status" value={cf.employment_status} onChange={(v) => set("employment_status", v)} options={EMPLOYMENT_STATUS} />
          <Sel label="Industry" testid="cf-industry" value={cf.industry} onChange={(v) => set("industry", v)} options={INDUSTRY} />
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Professional title <span className="text-[#9E978E]">(optional, no employer name)</span></label>
            <input data-testid="cf-professional-title" maxLength={80} value={cf.professional_title}
              onChange={(e) => set("professional_title", e.target.value)} className={inputCls} placeholder="e.g. Senior Architect" />
          </div>
          <Sel label="Years of professional experience" testid="cf-years-experience" value={cf.years_experience} onChange={(v) => set("years_experience", v)} options={YEARS_EXPERIENCE} />
          <Sel label="Tenure with current employer/business" testid="cf-employer-tenure" value={cf.employer_tenure} onChange={(v) => set("employer_tenure", v)} options={EMPLOYER_TENURE} />
          <Sel label="Education" testid="cf-education" value={cf.education} onChange={(v) => set("education", v)} options={EDUCATION} />
          <Sel label="Business ownership" testid="cf-business-ownership" value={cf.business_ownership} onChange={(v) => set("business_ownership", v)} options={BUSINESS_OWNERSHIP} />
        </div>
      </div>

      {/* Financial Profile */}
      <div className="space-y-3 pt-2 border-t border-white/8">
        <h4 className="text-sm text-[#C9A96E] flex items-center gap-1.5"><Lock size={14} className="text-[#751A32]" /> Financial Profile
          <span className="text-[10px] text-[#9E978E] font-normal">— private by default; set each field to "Approved members" to share</span>
        </h4>
        <div className="rounded-lg border border-white/8 bg-[#0C0A0A]/40 p-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Self-reported annual personal income (USD)</label>
            <VisibilityToggle testid="cf-vis-income_range" value={cf.fin_visibility.income_range} onChange={(v) => setVis("income_range", v)} />
          </div>
          <select data-testid="cf-income-range" value={cf.income_range || ""} onChange={(e) => set("income_range", e.target.value || null)} className={inputCls}>
            <option value="">Prefer not to disclose</option>
            {INCOME_RANGE.filter(([k]) => k !== "prefer_not_to_disclose").map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        </div>
        <ChecklistField label="Income sources" testid="cf-income-sources" options={INCOME_SOURCES}
          values={cf.income_sources} onToggle={(v) => toggleIn("income_sources", v)}
          visNode={<VisibilityToggle testid="cf-vis-income_sources" value={cf.fin_visibility.income_sources} onChange={(v) => setVis("income_sources", v)} />} />
        <div className="rounded-lg border border-white/8 bg-[#0C0A0A]/40 p-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Financial lifestyle</label>
            <VisibilityToggle testid="cf-vis-financial_lifestyle" value={cf.fin_visibility.financial_lifestyle} onChange={(v) => setVis("financial_lifestyle", v)} />
          </div>
          <select data-testid="cf-financial-lifestyle" value={cf.financial_lifestyle || ""} onChange={(e) => set("financial_lifestyle", e.target.value || null)} className={inputCls}>
            <option value="">Prefer not to say</option>
            {FINANCIAL_LIFESTYLE.filter(([k]) => k !== "prefer_not_to_say").map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        </div>
        <ChecklistField label="Financial goals" testid="cf-financial-goals" options={FINANCIAL_GOALS}
          values={cf.financial_goals} onToggle={(v) => toggleIn("financial_goals", v)}
          visNode={<VisibilityToggle testid="cf-vis-financial_goals" value={cf.fin_visibility.financial_goals} onChange={(v) => setVis("financial_goals", v)} />} />
        <div className="rounded-lg border border-white/8 bg-[#0C0A0A]/40 p-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Relationship approach to finances</label>
            <VisibilityToggle testid="cf-vis-finance_relationship_approach" value={cf.fin_visibility.finance_relationship_approach} onChange={(v) => setVis("finance_relationship_approach", v)} />
          </div>
          <select data-testid="cf-finance-relationship" value={cf.finance_relationship_approach || ""} onChange={(e) => set("finance_relationship_approach", e.target.value || null)} className={inputCls}>
            <option value="">Prefer not to disclose</option>
            {FINANCE_RELATIONSHIP.filter(([k]) => k !== "prefer_not_to_disclose").map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        </div>
      </div>

      <p className="text-[11px] text-[#9E978E] flex items-start gap-1.5">
        <ShieldCheck size={12} className="text-[#6FD79E] mt-0.5 shrink-0" />
        Private fields are never shown to anyone but you. "Approved members" fields are visible only to logged-in,
        approved DateBid members. We never display a verified badge for this self-reported information.
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <GoldButton data-testid="cf-save-btn" onClick={save} disabled={saving} className="sm:flex-1">{saving ? "Saving…" : "Save Career & Financial"}</GoldButton>
        {!confirmDel ? (
          <GhostButton data-testid="cf-remove-btn" onClick={() => setConfirmDel(true)} className="sm:w-auto">
            <span className="flex items-center gap-1.5"><Trash2 size={14} /> Remove all</span>
          </GhostButton>
        ) : (
          <div className="flex items-center gap-2 rounded-lg border border-[#E58CA0]/40 bg-[#1C171A] px-3 py-2" data-testid="cf-remove-confirm">
            <span className="text-[11px] text-[#E58CA0]">Permanently delete ALL career & financial info?</span>
            <button data-testid="cf-remove-confirm-yes" onClick={removeAll} className="text-xs text-[#E58CA0] underline">Yes, delete</button>
            <button data-testid="cf-remove-confirm-no" onClick={() => setConfirmDel(false)} className="text-xs text-[#9E978E]">Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
